import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import type { ColumnMetadata, GridFilter, GridFilterAtom, SortDescriptor } from '../types';

function columnFilterHasValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    const inner = (value as { value: unknown }).value;
    return inner !== undefined && inner !== null && inner !== '';
  }
  return true;
}

/** True when TanStack column filter row has at least one non-empty value. */
export function hasActiveColumnFilters(columnFilters: ColumnFiltersState): boolean {
  return columnFilters.some((filter) => columnFilterHasValue(filter.value));
}

/** True when the grid has column filters, sorting, or toolbar search applied. */
export function hasUserGridFilterState(options: {
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  searchTerm?: string;
}): boolean {
  const { sorting = [], columnFilters = [], searchTerm } = options;

  if (sorting.length > 0 || searchTerm?.trim() || hasActiveColumnFilters(columnFilters))
    return true;
  return false;
}

type FilterValueWithOperator = {
  operator?: string;
  value?: unknown;
  op?: string;
  val?: unknown;
};

export function isSortSelectorAllowedForColumns(
  selector: string,
  parsedColumns: ColumnMetadata[]
): boolean {
  if (!parsedColumns.length) return false;
  const column = parsedColumns.find((c) => (c.field || c.dataField) === selector);
  return column != null && column.allowSorting !== false;
}

export function toSortDescriptors(
  sortingState: SortingState,
  parsedColumns?: ColumnMetadata[]
): SortDescriptor[] {
  return sortingState
    .filter((sort) => {
      if (!parsedColumns?.length) return true;
      return isSortSelectorAllowedForColumns(sort.id, parsedColumns);
    })
    .map((sort) => ({
      selector: sort.id,
      desc: sort.desc,
    }));
}

export function toGridFilterFromColumnFilters(
  columnFilters: ColumnFiltersState,
  parsedColumns: ColumnMetadata[]
): GridFilter | undefined {
  if (!columnFilters.length) {
    return undefined;
  }

  const atoms: GridFilterAtom[] = [];

  columnFilters.forEach((filter) => {
    const raw = filter.value;
    if (raw === undefined || raw === null || raw === '') {
      return;
    }

    const columnMeta = parsedColumns.find((col) => {
      const key = col.field || col.dataField;
      return key === filter.id;
    });

    const isBoolean = columnMeta?.type === 'boolean' || columnMeta?.dataType === 'boolean';

    let operator = isBoolean ? '=' : 'contains';
    let value: unknown = raw;

    if (typeof raw === 'object' && raw !== null) {
      const typedRaw = raw as FilterValueWithOperator;
      if (typedRaw.operator !== undefined || typedRaw.value !== undefined) {
        operator = typedRaw.operator || operator;
        value = typedRaw.value;
      } else if (typedRaw.op !== undefined || typedRaw.val !== undefined) {
        operator = typedRaw.op || operator;
        value = typedRaw.val;
      }
    }

    if (value === undefined || value === null || value === '') {
      return;
    }

    atoms.push([filter.id, operator, value]);
  });

  if (!atoms.length) {
    return undefined;
  }

  if (atoms.length === 1) {
    return atoms[0];
  }

  const combined: (GridFilterAtom | 'and' | 'or')[] = [atoms[0]];
  for (let i = 1; i < atoms.length; i += 1) {
    combined.push('and', atoms[i]);
  }

  return combined;
}

export function toGlobalSearchFilter(
  searchTerm: string,
  parsedColumns: ColumnMetadata[]
): GridFilter | undefined {
  const term = searchTerm.trim();
  if (!term) {
    return undefined;
  }

  const searchableFields = Array.from(
    new Set(
      parsedColumns
        .filter((column) => column.canView !== false)
        .map((column) => column.field || column.dataField)
        .filter((field): field is string => Boolean(field))
    )
  );

  if (!searchableFields.length) {
    return undefined;
  }

  const atoms: GridFilterAtom[] = searchableFields.map((field) => [field, 'contains', term]);
  if (atoms.length === 1) {
    return atoms[0];
  }

  const combined: (GridFilterAtom | 'and' | 'or')[] = [atoms[0]];
  for (let i = 1; i < atoms.length; i += 1) {
    combined.push('or', atoms[i]);
  }

  return combined;
}

export function combineAndFilter(left?: GridFilter, right?: GridFilter): GridFilter | undefined {
  if (!left) return right;
  if (!right) return left;
  return [left, 'and', right] as unknown as GridFilter;
}
